import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { getCookie } from "../utils/api";
import LoginPopup from "../components/LoginPopup/LoginPopup";
import { setLoginOpener } from "../lib/api-interceptor";
import { isPlaceholderApiKeyExpiration } from "../utils/api-key-expiry";
import { refreshAuthxSessionCoalesced } from "../utils/authx-session-refresh";
import { hasSessionKeyCookies } from "../utils/session-reauth";

const PopupContext = createContext();

export function PopupProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);

  const handleOpenRef = useRef(async () => {});

  /**
   * Otwarcie popupu ponownego logowania: wymaga accessKey + refreshKey (sesja AuthX).
   */
  const handleOpen = useCallback(
    async (_type = "login") => {
      const path = location.pathname;

      if (
        path === "/" ||
        path === "/register" ||
        path === "/verify-email" ||
        path === "/verify-phone" ||
        path === "/mfa" ||
        path === "/reset-password" ||
        path.includes("bank-feeds-connected") ||
        path.includes("forgotuserpassword") ||
        path.includes("dev/test-errors") ||
        path.includes("dev/new-login-test")
      ) {
        return;
      }

      const storedTime = getCookie("apiKeyExpirationUTCDate");
      const accessKeyCookie = getCookie("accessKey");
      const refreshKeyCookie = getCookie("refreshKey");

      const missingExpiryLike =
        !storedTime ||
        `${storedTime}` === "null" ||
        isPlaceholderApiKeyExpiration(storedTime);

      if (
        missingExpiryLike &&
        ((!accessKeyCookie || accessKeyCookie === "undefined") ||
          (!refreshKeyCookie || refreshKeyCookie === "undefined"))
      ) {
        navigate("/");
        return;
      }

      if (
        !accessKeyCookie ||
        accessKeyCookie === "undefined" ||
        !refreshKeyCookie ||
        refreshKeyCookie === "undefined"
      ) {
        navigate("/");
        return;
      }

      if (path === "/mfa") {
        enqueueSnackbar("Session expired, please log in again.", {
          variant: "info",
        });
        navigate("/");
        return;
      }

      setOpen(true);
    },
    [location.pathname, navigate, enqueueSnackbar],
  );

  handleOpenRef.current = handleOpen;

  useEffect(() => {
    const opener = (type) => Promise.resolve(handleOpenRef.current?.(type));
    setLoginOpener(opener);
    return () => setLoginOpener(() => Promise.resolve());
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      return undefined;
    }
    const onFocus = () => {
      if (!hasSessionKeyCookies()) {
        return;
      }
      void refreshAuthxSessionCoalesced();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <PopupContext.Provider value={{ open, handleOpen, handleClose }}>
      {children}
      <LoginPopup open={open} handleClose={handleClose} />
    </PopupContext.Provider>
  );
}

export function useLoginPopup() {
  return useContext(PopupContext);
}
